import { useEffect, useState } from "react";
import DropdownInput from "../components/dropdownInput";
import ResolutionRadio from "../components/resolutionFilter";
import SideMenu from "../components/sideMenu";
import {
  ChartDataPoint,
  Condition,
  CustomApiResponse,
  Device,
  Organization,
  ResolutionFilter,
} from "../type";
import MultiLineChart from "../components/lineChartComponent";
import ColorDropdownInput from "../components/colorDropdownInput";
import { organizationService } from "../services/organizationService";
import { deviceService } from "../services/deviceService";
import Loading from "../components/loading";
import { getCustomTrendByRange } from "../services/dataService";
import { colors } from "../constant/color";

// --- Constants สำหรับ Pagination ---
const ORG_LIMIT = 10;
const DEVICE_LIMIT = 10;

const CustomTrendPage: React.FC = () => {
  const [resolution, setResolution] = useState<ResolutionFilter>(
    ResolutionFilter.HOURLY
  );
  
  // --- States สำหรับ Organization ---
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectOrgIdx, setSelectOrgIdx] = useState<number>();
  const [orgPage, setOrgPage] = useState(1);
  const [totalOrgs, setTotalOrgs] = useState(0);

  // --- States สำหรับ Device ---
  const [devices, setDevices] = useState<Device[]>([]);
  const [devicePage, setDevicePage] = useState(1);
  const [totalDevices, setTotalDevices] = useState(0);

  const [parametes, setParameters] = useState<string[]>([
    "co2Inlet",
    "o2Inlet",
    "co2RHInlet",
    "co2TempInlet",
    "o2TempInlet",
    "co2Outlet",
    "o2Outlet",
    "co2RHOutlet",
    "co2TempOutlet",
    "o2TempOutlet",
    "pm25Inlet",
    "pm25Outlet",
    "pm10Inlet",
    "pm10Outlet",
  ]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState<any[]>([]);
  const [lines, setLines] = useState<string[]>([]);
  const [conditions, setConditions] = useState<Condition[]>([
    { device: 0, parameter: 0, color: 0, line: 1 },
  ]);

  // --- Fetch Organizations (รองรับ Pagination) ---
  const fetchOrganizations = async (page = 1, append = false) => {
    try {
      setIsLoading(true);
      const orgs = await organizationService.getOrganizations({ page, limit: ORG_LIMIT });
      if (append) {
        setOrganizations((prev) => [...prev, ...orgs.data]);
      } else {
        setOrganizations([...orgs.data]);
        if (orgs.data.length > 0) setSelectOrgIdx(0);
      }
      setTotalOrgs(orgs.total ?? 0);
      return orgs.data;
    } catch (error) {
      console.error("Error fetching organizations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Fetch Devices (รองรับ Pagination) ---
  const fetchDevicesInOrg = async (page = 1, append = false, targetOrgIdx?: number, orgsList?: Organization[]) => {
    try {
      setIsLoading(true);
      const currentOrgIdx = targetOrgIdx !== undefined ? targetOrgIdx : selectOrgIdx;
      const currentOrgsList = orgsList || organizations;
      
      const orgName = currentOrgIdx !== undefined && currentOrgsList[currentOrgIdx]
          ? currentOrgsList[currentOrgIdx].orgName
          : undefined;

      const devicesRes = await deviceService.getDevices({
        org_name: orgName,
        page,
        limit: DEVICE_LIMIT
      });

      if (append) {
        setDevices((prev) => [...prev, ...devicesRes.data]);
      } else {
        setDevices(devicesRes.data);
      }
      setTotalDevices(devicesRes.total ?? 0);
    } catch (error) {
      console.error("Error fetching devices:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Handlers สำหรับ Infinite Scroll ---
  const handleLoadMoreOrgs = () => {
    if (organizations.length < totalOrgs) {
      const nextPage = orgPage + 1;
      setOrgPage(nextPage);
      fetchOrganizations(nextPage, true);
    }
  };

  const handleLoadMoreDevices = () => {
    if (devices.length < totalDevices) {
      const nextPage = devicePage + 1;
      setDevicePage(nextPage);
      fetchDevicesInOrg(nextPage, true);
    }
  };

  const addNewLine = () => {
    setConditions([
      ...conditions,
      { device: 0, parameter: 0, color: 0, line: conditions.length + 1 },
    ]);
  };

  const removeLine = (index: number) => {
    let newConditions = [...conditions];
    newConditions.splice(index, 1);
    setConditions(newConditions);
  };

  const initialize = async () => {
    try {
      const fetchedOrgs = await fetchOrganizations(1, false);
      if (fetchedOrgs && fetchedOrgs.length > 0) {
         await fetchDevicesInOrg(1, false, 0, fetchedOrgs);
      }
    } catch (error) {
      console.error("Initialization failed:", error);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const fetchCustomtrend = async () => {
    try {
      const newConditions = conditions.map((c) => ({
        line: c.line,
        parameter: parametes[c.parameter],
        // ป้องกัน Error กรณี device index ไม่ถูกต้อง
        deviceId: devices[c.device]?.deviceId ?? 0, 
      }));
      const customTrend = await getCustomTrendByRange(
        newConditions,
        startDate,
        endDate,
        resolution
      );
      return customTrend;
    } catch (error) {
      console.log("Fetch custom trend error", error);
    }
  };

  const onSelectDevice = async (deviceIdx: number, lineIdx: number) => {
    let newCondition = [...conditions];
    newCondition[lineIdx].device = deviceIdx;
    setConditions(newCondition);
  };

  const onSelectOrg = async (orgIdx: number) => {
    setSelectOrgIdx(orgIdx);
    setDevicePage(1);
    // รีเซ็ต conditions เพื่อป้องกัน index device เก่าค้างใน org ใหม่
    setConditions([{ device: 0, parameter: 0, color: 0, line: 1 }]);
    await fetchDevicesInOrg(1, false, orgIdx);
  };

  const onSelectParameter = async (parameter: number, lineIdx: number) => {
    let newCondition = [...conditions];
    newCondition[lineIdx].parameter = parameter;
    setConditions(newCondition);
  };

  const onSelectColor = async (color: number, lineIdx: number) => {
    let newCondition = [...conditions];
    newCondition[lineIdx].color = color;
    setConditions(newCondition);
  };

  const searchData = async () => {
    try {
      setIsLoading(true);
      if (selectOrgIdx !== undefined) {
        const trend = await fetchCustomtrend();
        if (trend) {
          const { chartData, lines } = transformToChartData(trend);
          setData(chartData);
          setLines(lines);
        } else {
          setData([]);
          setLines([]);
        }
      }
      setIsLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  function transformToChartData(apiData: CustomApiResponse): {
    chartData: ChartDataPoint[];
    lines: string[];
  } {
    if (!apiData || !apiData.length) return { chartData: [], lines: [] };

    const maxLength = Math.max(...apiData.map((line) => line.data.length));
    const chartData: ChartDataPoint[] = [];
    const lines: string[] = [];

    for (let i = 0; i < maxLength; i++) {
      const point: ChartDataPoint = {
        name: "",
      };

      apiData.forEach((lineData) => {
        const entry = lineData.data[i];
        if (entry) {
          const timestamp = Object.keys(entry)[0];
          const value = Object.values(entry)[0];

          if (point.name === "") point.name = timestamp;

          const lineKey = `Line${lineData.line}`;
          point[lineKey] = parseFloat(value.toFixed(2));

          if (!lines.includes(lineKey)) {
            lines.push(lineKey);
          }
        }
      });

      // Ensure all lines exist on the point
      lines.forEach((lineKey) => {
        if (!(lineKey in point)) {
          point[lineKey] = 0;
        }
      });

      chartData.push(point);
    }

    return { chartData, lines };
  }

  return (
    <>
      <SideMenu active={2}>
        <div className="relative">
          {isLoading && <Loading />}
          <div className="container p-4 pt-8 xl:pt-4">
            {/* Header */}
            <div className="flex xl:flex-row flex-col gap-4 justify-between items-start">
              <h3 className="text-xl font-bold">Custom Trend Graph</h3>
              {selectOrgIdx !== undefined && (
                <div className="lg:w-64 w-full">
                  <DropdownInput
                    options={organizations.map((org) => org.orgName)}
                    onSelect={(e) => onSelectOrg(e)}
                    value_idx={selectOrgIdx}
                    onReachBottom={handleLoadMoreOrgs} // เพิ่ม load more
                  />
                </div>
              )}
            </div>

            {/* Date Range & Resolution */}
            <div className="mt-5 flex md:flex-row flex-col gap-5">
              <div className="flex flex-col lg:w-1/4">
                <p className="mb-2">Start Date</p>
                <input
                  type="datetime-local"
                  name="start"
                  id="start"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-nor focus:outline-none px-3 py-1 rounded"
                />
              </div>
              <div className="flex flex-col lg:w-1/4">
                <p className="mb-2">End Date</p>
                <input
                  type="datetime-local"
                  name="end"
                  id="end"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-nor focus:outline-none px-3 py-1 rounded"
                />
              </div>
            </div>
            <div className="mt-5">
              <p className="mb-2">Resolution</p>
              <ResolutionRadio
                is_mins={true}
                resolution={resolution}
                setResolution={setResolution}
              />
            </div>
            <br />

            {/* Conditions Builder */}
            <div className="shadow-lg w-[380px] md:w-full">
              <div
                className="bg-gradient-to-r from-[#74CE90] to-[#18A2A7] rounded-t-lg
                    text-white flex justify-between p-5 py-6 items-center"
              >
                <p className="text-xl">Condition</p>
              </div>
              <div className="p-5 w-full">
                {/* Header table แสดงเฉพาะ md ขึ้นไป */}
                <div className="hidden md:grid md:grid-cols-8 w-full gap-5 mb-4 text-sm font-semibold">
                  <p className="col-span-1">Line</p>
                  <p className="col-span-2">Device</p>
                  <p className="col-span-2">Parameter</p>
                  <p className="col-span-2">Color</p>
                  <p className="col-span-1"></p>
                </div>

                {/* Rows */}
                <div className="flex flex-col gap-y-4">
                  {conditions.map((c, i) => (
                    <div
                      key={i}
                      className="w-full grid grid-cols-1 md:grid-cols-8 gap-3 md:gap-5 items-center rounded-lg border border-gray-light md:border-0 md:rounded-none p-3 md:p-0"
                    >
                      {/* Line */}
                      <div className="flex justify-between">
                        <p className="md:col-span-1 text-sm font-semibold md:font-normal">
                          <span className="inline-block md:hidden mr-2 text-gray-500">
                            Line
                          </span>
                          {i + 1}
                        </p>
                        <p className="md:col-span-1 flex justify-end items-center md:hidden">
                          <img
                            src="./trash.png"
                            alt="remove"
                            className="w-6 cursor-pointer"
                            onClick={() => removeLine(i)}
                          />
                        </p>
                      </div>

                      {/* Device */}
                      <div className="md:col-span-2">
                        <p className="text-xs text-gray-500 mb-1 md:hidden">
                          Device
                        </p>
                        <DropdownInput
                          options={devices.map((d) => d.deviceName)}
                          value_idx={c.device}
                          onSelect={(e) => onSelectDevice(e, i)}
                          onReachBottom={handleLoadMoreDevices} // เพิ่ม load more
                        />
                      </div>

                      {/* Parameter */}
                      <div className="md:col-span-2">
                        <p className="text-xs text-gray-500 mb-1 md:hidden">
                          Parameter
                        </p>
                        <DropdownInput
                          options={parametes}
                          onSelect={(e) => onSelectParameter(e, i)}
                          value_idx={c.parameter}
                        />
                      </div>

                      {/* Color */}
                      <div className="md:col-span-2">
                        <p className="text-xs text-gray-500 mb-1 md:hidden">
                          Color
                        </p>
                        <ColorDropdownInput
                          color_idx={c.color}
                          onSelect={(e) => onSelectColor(e, i)}
                        />
                      </div>

                      {/* Trash */}
                      <p className="md:col-span-1 flex justify-end items-center hidden md:flex">
                        <img
                          src="./trash.png"
                          alt="remove"
                          className="w-6 cursor-pointer"
                          onClick={() => removeLine(i)}
                        />
                      </p>
                    </div>
                  ))}
                </div>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 my-5 mt-8">
                  <button
                    className="flex gap-2 border border-dark-green px-3 py-1.5 rounded-lg justify-center items-center"
                    onClick={() => addNewLine()}
                  >
                    <img src="./add.svg" alt="" className="w-5" />
                    <p className="text-dark-green text-sm">Add new line</p>
                  </button>

                  <button
                    className="rounded-lg bg-[#74D15C] w-full sm:w-32 px-3 py-1.5 text-sm text-white font-medium self-end"
                    onClick={() => searchData()}
                  >
                    SEARCH
                  </button>
                </div>
              </div>
            </div>
            <br />
            
            {/* Chart Area */}
            {data.length > 0 ? (
              <div className="my-5 overflow-x-auto w-[380px] md:w-full border border-gray-light rounded px-4 py-6">
                <MultiLineChart
                  data={data}
                  lines={lines}
                  colors={conditions.map((c) => colors[c.color])}
                />
              </div>
            ) : (
              <div className="text-center mt-12 text-gray-nor">No Data</div>
            )}
          </div>
        </div>
      </SideMenu>
    </>
  );
};

export default CustomTrendPage;